// src/pages/Orders/OrderDetails.jsx - REFACTORED VERSION WITH KARIGAR
// ✅ Fully Responsive Mobile-First Design
// ✅ Split tabs into separate components for better maintainability
// ✅ Fixed "Mark as Delivered" button display logic
// ✅ Added REFUND_INITIATED legacy status support
// ✅ All refund statuses updated to use REFUND_ prefix
// ✅ NEW: Karigar assignment and management integration

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderByIdForAdminThunk,
  updateOrderStatusThunk,
  updatePaymentStatusThunk,
  approveOrderThunk,
  rejectOrderThunk,
  finalizeOrderThunk,
  approveRefundThunk,
  completeRefundThunk,
  cancelRefundThunk,
  getRefundDetailsThunk,
} from "../../store/thunks/orderThunks";

// ============================================================================
// KARIGAR IMPORTS
// ============================================================================
import { useOrderKarigar } from "../../hooks/useOrderKarigar";
import {
  selectOrderKarigarAssignments,
  selectOrderItemsWithoutKarigar,
  selectOrderItemsWithKarigar,
  selectItemsWithKarigarStatus,
  selectCanAssignKarigarToOrder,
  selectAllItemsHaveKarigar,
  selectItemsNeedingKarigarCount,
  selectAllKarigarWorkComplete,
  selectOrderWithKarigarSummary,
} from "../../store/selectors/orderSelectors";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import Modal from "../../components/common/Modal";
import {
  notifySuccess,
  notifyError,
  notifyInfo,
} from "../../utils/notification";
import FulfillmentManager from "./components/FulfillmentManager";

// Import all tab components
import {
  OverviewTab,
  ItemsTab,
  ExchangeTab,
  PaymentTab,
  RefundTab,
  ApprovalTab,
  TimelineTab,
  KarigarTab,
} from "./components/tabs";

// Import modals and other components
import PaymentStatusCard from "./components/PaymentStatusCard";
import QuickActionsBar from "./components/QuickActionsBar";
import PaymentUpdateModal from "./components/PaymentUpdateModal";
import RefundMethodModal from "./components/RefundMethodModal";
import KarigarAssignmentModal from "./components/modals/KarigarAssignmentModal";
import KarigarQueueModal from "./components/modals/KarigarQueueModal";
import BillExport from "./components/BillExport";

import {
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  TruckIcon,
  CurrencyRupeeIcon,
  ShoppingBagIcon,
  UserIcon,
  ClockIcon,
  DocumentTextIcon,
  RefreshIcon,
  PrinterIcon,
  UserGroupIcon,
  DotsVerticalIcon,
  ChevronDownIcon,
} from "@heroicons/react/outline";
import { BASE_API_URL } from "services/api";

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { order = null, loading = false } = useSelector(
    (state) => state.orders || {},
  );

  // State management
  const [activeTab, setActiveTab] = useState("overview");
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRefundMethodModal, setShowRefundMethodModal] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loadingPaymentStatus, setLoadingPaymentStatus] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [showBill, setShowBill] = useState(false);

  // Refund method state
  const [refundMethod, setRefundMethod] = useState("CASH");
  const [refundBankDetails, setRefundBankDetails] = useState({
    accountName: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
  });
  const [refundUpiDetails, setRefundUpiDetails] = useState({
    vpa: "",
    verifiedName: "",
  });
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // ============================================================================
  // KARIGAR HOOK & SELECTORS
  // ============================================================================
  const {
    bulkProductKarigars,
    bulkOtherKarigars,
    bulkRecommendations,
    bulkSummary,
    orderKarigarStatus,
    karigarQueue,
    loading: karigarLoading,
    error: karigarError,
    showAssignmentModal,
    selectedItemIndex,
    showQueueModal,
    selectedKarigarId,
    getBulkAvailability,
    assignViaOrder,
    reassignForOrderItem,
    getOrderStatus,
    getQueue,
    openAssignmentModal,
    closeAssignmentModal,
    openQueueModal,
    closeQueueModal,
    clearAll: clearAllKarigar,
  } = useOrderKarigar();

  // Order karigar selectors
  const karigarAssignments = useSelector(selectOrderKarigarAssignments);
  const itemsWithoutKarigar = useSelector(selectOrderItemsWithoutKarigar);
  const itemsWithKarigar = useSelector(selectOrderItemsWithKarigar);
  const itemsWithKarigarStatus = useSelector(selectItemsWithKarigarStatus);
  const canAssignKarigar = useSelector(selectCanAssignKarigarToOrder);
  const allItemsHaveKarigar = useSelector(selectAllItemsHaveKarigar);
  const itemsNeedingKarigarCount = useSelector(selectItemsNeedingKarigarCount);
  const allKarigarWorkComplete = useSelector(selectAllKarigarWorkComplete);
  const orderWithKarigarSummary = useSelector(selectOrderWithKarigarSummary);

  // Load order
  useEffect(() => {
    if (orderId) {
      dispatch(getOrderByIdForAdminThunk(orderId));
    }
  }, [orderId, dispatch]);

  // Load payment status when order loads
  useEffect(() => {
    if (order?.orderId) {
      checkPaymentStatus(order.orderId);
    }
  }, [order?.orderId]);

  // Load karigar status when order loads
  useEffect(() => {
    if (order?.orderId && order?.karigarAssignments?.length > 0) {
      getOrderStatus(order.orderId);
    }
  }, [order?.orderId, order?.karigarAssignments?.length, getOrderStatus]);

  // Cleanup karigar state on unmount
  useEffect(() => {
    return () => {
      clearAllKarigar();
    };
  }, [clearAllKarigar]);

  // ============================================================================
  // Payment Status Check Function
  // ============================================================================
  const checkPaymentStatus = async (orderIdToCheck) => {
    try {
      setLoadingPaymentStatus(true);
      const response = await fetch(
        `${BASE_API_URL}/orders/admin/order/${orderIdToCheck}/payment-status`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch payment status");
      }

      const data = await response.json();
      setPaymentStatus(data.data);
      return data;
    } catch (error) {
      console.error("Payment status check error:", error);
      return {
        data: {
          allowedActions: {},
          warnings: [],
          blockers: [],
        },
      };
    } finally {
      setLoadingPaymentStatus(false);
    }
  };

  // ============================================================================
  // Enhanced Status Update Handler with Payment Validation
  // ============================================================================
  const handleUpdateStatus = async (
    newStatus,
    refundDetailsProvided = null,
  ) => {
    try {
      if (
        newStatus === "CONFIRMED" &&
        order.refundDetails?.required &&
        !refundDetailsProvided
      ) {
        setPendingStatusChange(newStatus);
        setShowRefundMethodModal(true);
        return;
      }

      if (newStatus === "DELIVERED") {
        const paymentCheck = await checkPaymentStatus(order.orderId);

        if (!paymentCheck.data?.allowedActions?.canDeliver) {
          const blocker = paymentCheck.data?.blockers?.find(
            (b) => b.level === "CRITICAL_BLOCKER",
          );

          if (blocker) {
            notifyError(
              <div>
                <p className="font-semibold">🚫 {blocker.message}</p>
                <p className="text-sm mt-1">{blocker.action}</p>
              </div>,
              { duration: 8000 },
            );
            setShowPaymentModal(true);
            return;
          }
        }
      }

      if (newStatus === "CONFIRMED" && !order.refundDetails?.required) {
        const paymentCheck = await checkPaymentStatus(order.orderId);

        if (
          paymentCheck.data?.warnings &&
          paymentCheck.data.warnings.length > 0
        ) {
          const warning = paymentCheck.data.warnings[0];

          if (warning.canBypass) {
            const proceed = window.confirm(
              `⚠️ ${warning.message}\n\n${warning.action}\n\nDo you want to proceed anyway?`,
            );

            if (!proceed) return;
          }
        }
      }

      const updatePayload = {
        orderId: order.orderId,
        status: newStatus,
        comment: `Status updated to ${newStatus}`,
      };

      if (refundDetailsProvided) {
        updatePayload.refundDetails = refundDetailsProvided;
      }

      const result = await dispatch(
        updateOrderStatusThunk(updatePayload),
      ).unwrap();

      notifySuccess(`Order status updated to ${newStatus}`);

      if (result.data?.paymentInfo && !result.data.paymentInfo.fullyPaid) {
        notifyInfo(
          `Payment: ${
            result.data.paymentInfo.status
          } (₹${result.data.paymentInfo.amountDue.toLocaleString()} due)`,
          { duration: 5000 },
        );
      }

      if (result.data?.nextSteps) {
        notifyInfo(
          result.data.nextSteps.payment || result.data.nextSteps.message,
          { duration: 5000 },
        );
      }

      dispatch(getOrderByIdForAdminThunk(orderId));
      checkPaymentStatus(order.orderId);
    } catch (error) {
      if (error.response?.data) {
        const err = error.response.data;
        notifyError(
          <div className="space-y-2">
            <p className="font-semibold text-red-700">{err.error}</p>
            {err.details?.message && (
              <p className="text-sm">{err.details.message}</p>
            )}
            {err.refundDue && (
              <p className="text-sm text-orange-600">
                Refund Due: ₹{err.refundDue.toLocaleString()}
              </p>
            )}
            {err.action?.required && (
              <p className="text-xs text-blue-600 mt-2">
                <strong>Action:</strong> {err.action.required}
              </p>
            )}
          </div>,
          { duration: 10000 },
        );
      } else {
        notifyError(error || "Failed to update status");
      }
    }
  };

  // ============================================================================
  // Handle Refund Method Confirmation
  // ============================================================================
  const handleConfirmWithRefundMethod = () => {
    if (refundMethod === "BANK_TRANSFER") {
      if (
        !refundBankDetails.accountName ||
        !refundBankDetails.accountNumber ||
        !refundBankDetails.ifscCode ||
        !refundBankDetails.bankName
      ) {
        notifyError("Please fill all bank details");
        return;
      }
    } else if (refundMethod === "UPI") {
      if (!refundUpiDetails.vpa) {
        notifyError("Please enter UPI ID");
        return;
      }
    }

    const refundDetails = {
      refundMethod,
      refundReason: order.refundDetails.refundReason || "EXCESS_EXCHANGE_VALUE",
    };

    if (refundMethod === "BANK_TRANSFER") {
      refundDetails.bankDetails = refundBankDetails;
    } else if (refundMethod === "UPI") {
      refundDetails.upiDetails = refundUpiDetails;
    }

    setShowRefundMethodModal(false);
    handleUpdateStatus(pendingStatusChange, refundDetails);
  };

  // ============================================================================
  // Enhanced Payment Update Handler
  // ============================================================================
  const handleUpdatePayment = async (paymentData) => {
    try {
      const result = await dispatch(
        updatePaymentStatusThunk({
          orderId: order.orderId,
          paymentData,
        }),
      ).unwrap();

      if (result.data?.orderStatusChanged) {
        const { from, to, reason } = result.data.orderStatusChanged;

        notifySuccess(
          <div>
            <p className="font-semibold">Payment Updated Successfully!</p>
            <p className="text-sm mt-1">
              Order automatically progressed from <strong>{from}</strong> to{" "}
              <strong>{to}</strong>
            </p>
            {reason && <p className="text-xs text-gray-600 mt-1">{reason}</p>}
          </div>,
          { duration: 6000 },
        );
      } else {
        notifySuccess("Payment status updated");
      }

      if (result.data?.nextSteps) {
        notifyInfo(
          result.data.nextSteps.message || result.data.nextSteps.nextAction,
          { duration: 5000 },
        );
      }

      setShowPaymentModal(false);
      dispatch(getOrderByIdForAdminThunk(orderId));
      checkPaymentStatus(order.orderId);
    } catch (error) {
      if (error.response?.data) {
        const err = error.response.data;

        notifyError(
          <div className="space-y-2">
            <p className="font-semibold">{err.error}</p>
            {err.details?.message && (
              <p className="text-sm">{err.details.message}</p>
            )}
            {err.action?.required && (
              <p className="text-xs text-blue-600 mt-2">
                ✓ {err.action.required}
              </p>
            )}
          </div>,
          { duration: 8000 },
        );
      } else {
        notifyError(error.message || "Failed to update payment");
      }
    }
  };

  // Other handlers
  const handleBack = () => {
    navigate("/orders");
  };

  const handleEdit = () => {
    navigate(`/orders/edit/${orderId}`);
  };

  const handleApprove = async () => {
    try {
      await dispatch(
        approveOrderThunk({
          orderId: order.orderId,
          notes: approvalNotes,
        }),
      ).unwrap();
      notifySuccess("Order approved successfully");
      setShowApprovalModal(false);
      dispatch(getOrderByIdForAdminThunk(orderId));
    } catch (error) {
      notifyError(error || "Failed to approve order");
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      notifyError("Please provide a rejection reason");
      return;
    }

    try {
      await dispatch(
        rejectOrderThunk({
          orderId: order.orderId,
          reason: rejectionReason,
        }),
      ).unwrap();
      notifySuccess("Order rejected");
      setShowRejectModal(false);
      dispatch(getOrderByIdForAdminThunk(orderId));
    } catch (error) {
      notifyError(error || "Failed to reject order");
    }
  };

  const handleFinalize = async () => {
    if (
      !window.confirm(
        "Are you sure you want to finalize this order? This action will lock prices and confirm the order.",
      )
    ) {
      return;
    }

    try {
      await dispatch(finalizeOrderThunk(order.orderId)).unwrap();
      notifySuccess("Order finalized successfully");
      dispatch(getOrderByIdForAdminThunk(orderId));
    } catch (error) {
      notifyError(error || "Failed to finalize order");
    }
  };

  const handleApproveRefund = () => {
    navigate(`/orders/${orderId}/refund/approve`);
  };

  const handleProcessRefund = () => {
    navigate(`/orders/${orderId}/refund/process`);
  };

  // ============================================================================
  // KARIGAR HANDLERS
  // ============================================================================
  const handleOpenKarigarAssignment = useCallback(
    (itemIndex, productId) => {
      openAssignmentModal(itemIndex, productId);

      const item = order?.items?.[itemIndex];
      if (item && order) {
        getBulkAvailability({
          branchCode: order.branchCode,
          availableBranches: order.availableBranches || [order.branchCode],
          productType:
            item.product?.productType || item.productSnapshot?.productType,
          productSubType:
            item.product?.productSubType ||
            item.productSnapshot?.productSubType,
          workType: "NEW_MAKING",
          complexity: item.product?.manufacturing?.complexity || "medium",
          requiredSkills: item.product?.manufacturing?.requiredSkills || [],
          targetDate: order.expectedDeliveryDate,
          capableKarigars: item.product?.manufacturing?.capableKarigars || [],
          estimatedMakingTime: item.product?.manufacturing?.estimatedMakingTime,
        });
      }
    },
    [order, openAssignmentModal, getBulkAvailability],
  );

  const handleAssignKarigar = async (karigarId, workDetails = {}) => {
    if (selectedItemIndex === null || !order) return;

    try {
      await assignViaOrder(order.orderId, selectedItemIndex, karigarId, {
        ...workDetails,
        workType: workDetails.workType || "NEW_MAKING",
        expectedCompletionDate:
          workDetails.expectedCompletionDate || order.expectedDeliveryDate,
      });

      notifySuccess("Karigar assigned successfully!");
      closeAssignmentModal();
      dispatch(getOrderByIdForAdminThunk(orderId));
    } catch (error) {
      notifyError(error?.message || "Failed to assign karigar");
    }
  };

  const handleViewKarigarQueue = useCallback(
    (karigarId) => {
      openQueueModal(karigarId);
      getQueue(karigarId);
    },
    [openQueueModal, getQueue],
  );

  const handleUpdateKarigarStatus = async (orderId, assignmentId, status) => {
    try {
      const response = await fetch(
        `${BASE_API_URL}/orders/${orderId}/karigar/${assignmentId}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update karigar status");
      }

      notifySuccess(`Status updated to ${status}`);
      dispatch(getOrderByIdForAdminThunk(orderId));
    } catch (error) {
      notifyError(error.message || "Failed to update status");
      throw error;
    }
  };

  // Utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatRefundStatus = (status) => {
    if (!status) return "";
    return status.replace("REFUND_", "");
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: "gray",
      PENDING_APPROVAL: "yellow",
      APPROVED: "green",
      REJECTED: "red",
      PENDING: "blue",
      CONFIRMED: "green",
      PROCESSING: "blue",
      READY: "purple",
      DISPATCHED: "indigo",
      DELIVERED: "green",
      CANCELLED: "red",
    };
    return colors[status] || "gray";
  };

  const getPaymentColor = (status) => {
    const colors = {
      PENDING: "yellow",
      PARTIAL: "orange",
      COMPLETED: "green",
      FAILED: "red",
      REFUNDED: "purple",
    };
    return colors[status] || "gray";
  };

  const getRefundColor = (status) => {
    const colors = {
      REFUND_PENDING: "orange",
      REFUND_PENDING_APPROVAL: "yellow",
      REFUND_APPROVED: "blue",
      REFUND_INITIATED: "indigo",
      REFUND_PROCESSING: "indigo",
      REFUND_COMPLETED: "green",
      REFUND_FAILED: "red",
      REFUND_REJECTED: "red",
      REFUND_CANCELLED: "gray",
    };
    return colors[status] || "gray";
  };

  const getKarigarStatusColor = (status) => {
    const colors = {
      PENDING: "yellow",
      ACCEPTED: "blue",
      IN_PROGRESS: "indigo",
      COMPLETED: "green",
      CANCELLED: "red",
      REJECTED: "red",
    };
    return colors[status] || "gray";
  };

  // Loading State
  if (loading || !order) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-primary border-t-transparent mx-auto"></div>
          <p className="mt-3 text-sm sm:text-base text-gray-600">
            Loading order details...
          </p>
        </div>
      </div>
    );
  }

  // Calculate totals
  const totalExchangeValue =
    order.exchangeItems?.reduce(
      (sum, item) => sum + (item.agreedValue || 0),
      0,
    ) || 0;

  const hasExchange = order.exchangeItems && order.exchangeItems.length > 0;
  const hasRefund = order.refundDetails?.required;
  const hasKarigarAssignments =
    karigarAssignments && karigarAssignments.length > 0;
  const needsKarigarWork = order.items?.some(
    (item) =>
      item.product?.manufacturing?.requiresKarigar ||
      item.productSnapshot?.manufacturing?.requiresKarigar,
  );

  const tabs = [
    { id: "overview", label: "Overview", icon: DocumentTextIcon },
    { id: "items", label: "Items", icon: ShoppingBagIcon },
    {
      id: "karigar",
      label: "Karigar",
      icon: UserGroupIcon,
      count: hasKarigarAssignments ? karigarAssignments.length : 0,
      show: needsKarigarWork || hasKarigarAssignments,
      badge:
        itemsNeedingKarigarCount > 0
          ? `${itemsNeedingKarigarCount} pending`
          : null,
    },
    {
      id: "exchange",
      label: "Exchange",
      icon: RefreshIcon,
      count: order.exchangeItems?.length || 0,
      show: hasExchange,
    },
    { id: "payment", label: "Payment", icon: CurrencyRupeeIcon },
    {
      id: "refund",
      label: "Refund",
      icon: RefreshIcon,
      show: hasRefund,
      badge:
        order.refundDetails?.refundStatus === "REFUND_PENDING" ? "⚠️" : null,
    },
    {
      id: "fulfillment",
      label: "Fulfillment",
      icon: TruckIcon,
      show: order.fulfillment?.type,
    },
    {
      id: "approval",
      label: "Approval",
      icon: CheckCircleIcon,
      show: order.approval?.required,
    },
    { id: "timeline", label: "Timeline", icon: ClockIcon },
  ];

  const visibleTabs = tabs.filter((tab) => tab.show !== false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 top-0 z-30">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4">
          {/* Back Button & Order ID */}
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-900 text-sm sm:text-base"
            >
              <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              <span className="hidden sm:inline">Back to Orders</span>
              <span className="sm:hidden">Back</span>
            </button>

            {/* Mobile Actions Menu */}
            <div className="flex items-center gap-2 sm:hidden">
              <Button
                variant="outline"
                size="sm"
                className="p-2"
                onClick={() => setShowMobileActions(!showMobileActions)}
              >
                <DotsVerticalIcon className="w-5 h-5" />
              </Button>
            </div>

            {/* Desktop Actions */}
            <div className="hidden sm:flex gap-2 flex-wrap">
              {hasRefund &&
                order.refundDetails.refundStatus ===
                  "REFUND_PENDING_APPROVAL" && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleApproveRefund}
                    className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    Approve Refund
                  </Button>
                )}

              {hasRefund &&
                (order.refundDetails.refundStatus === "REFUND_APPROVED" ||
                  order.refundDetails.refundStatus === "REFUND_PENDING") && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleProcessRefund}
                    className="text-xs sm:text-sm"
                  >
                    <CurrencyRupeeIcon className="w-4 h-4 mr-1" />
                    Process Refund
                  </Button>
                )}

              {order.status === "DRAFT" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  className="text-xs sm:text-sm"
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}

              {order.status === "PENDING_APPROVAL" && (
                <>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowRejectModal(true)}
                    className="text-xs sm:text-sm"
                  >
                    <XCircleIcon className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => setShowApprovalModal(true)}
                    className="text-xs sm:text-sm"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                </>
              )}

              {(order.status === "APPROVED" ||
                (order.status === "DRAFT" && !order.approval?.required)) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleFinalize}
                  className="text-xs sm:text-sm"
                >
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  Finalize
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBill(true)}
                className="text-xs sm:text-sm"
              >
                <PrinterIcon className="w-4 h-4 mr-1" />
                Print
              </Button>
            </div>
          </div>

          {/* Order Title & Badges */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1 sm:mb-2">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">
                  {order.orderId}
                </h1>
                <Badge
                  variant={getStatusColor(order.status)}
                  size="sm"
                  className="text-[10px] sm:text-xs"
                >
                  {order.status}
                </Badge>
                {order.approval?.required && (
                  <Badge
                    variant="yellow"
                    size="sm"
                    className="text-[10px] sm:text-xs hidden sm:inline-flex"
                  >
                    Needs Approval
                  </Badge>
                )}
                {hasRefund && (
                  <Badge
                    variant={getRefundColor(order.refundDetails.refundStatus)}
                    size="sm"
                    className="text-[10px] sm:text-xs"
                  >
                    Refund{" "}
                    {formatRefundStatus(order.refundDetails.refundStatus)}
                  </Badge>
                )}
              </div>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                Created {formatDate(order.createdAt)} •{" "}
                {order.userId?.name || order.guestCustomer?.name || "Guest"}
              </p>
            </div>

            {/* Karigar Badge - Desktop */}
            {hasKarigarAssignments && (
              <div className="hidden sm:block">
                <Badge
                  variant={
                    allKarigarWorkComplete
                      ? "green"
                      : allItemsHaveKarigar
                        ? "blue"
                        : "yellow"
                  }
                  size="sm"
                >
                  {allKarigarWorkComplete
                    ? "Karigar Complete"
                    : allItemsHaveKarigar
                      ? "Karigar Assigned"
                      : `${itemsNeedingKarigarCount} Need Karigar`}
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Actions Dropdown */}
        {showMobileActions && (
          <div className="sm:hidden border-t border-gray-200 bg-gray-50 px-3 py-2">
            <div className="flex flex-wrap gap-2">
              {hasRefund &&
                order.refundDetails.refundStatus ===
                  "REFUND_PENDING_APPROVAL" && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleApproveRefund}
                    className="text-xs flex-1"
                  >
                    Approve Refund
                  </Button>
                )}
              {order.status === "DRAFT" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  className="text-xs flex-1"
                >
                  Edit
                </Button>
              )}
              {order.status === "PENDING_APPROVAL" && (
                <>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowRejectModal(true)}
                    className="text-xs flex-1"
                  >
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => setShowApprovalModal(true)}
                    className="text-xs flex-1"
                  >
                    Approve
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBill(true)}
                className="text-xs flex-1"
              >
                Print
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6">
        {/* Payment Status Card */}
        {paymentStatus && !loadingPaymentStatus && (
          <div className="mb-3 sm:mb-4 md:mb-6">
            <PaymentStatusCard
              paymentStatus={paymentStatus}
              order={order}
              formatCurrency={formatCurrency}
              getPaymentColor={getPaymentColor}
              onUpdatePayment={() => setShowPaymentModal(true)}
            />
          </div>
        )}

        {/* Alert Banners */}
        <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4 md:mb-6">
          {/* Karigar Alert */}
          {canAssignKarigar && itemsNeedingKarigarCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-start gap-2.5 sm:gap-3">
                <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-amber-900 text-sm sm:text-base mb-0.5 sm:mb-1">
                    Karigar Assignment Needed
                  </h3>
                  <p className="text-xs sm:text-sm text-amber-800 mb-2">
                    {itemsNeedingKarigarCount} item(s) require karigar
                    assignment
                  </p>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => setActiveTab("karigar")}
                    className="bg-amber-600 hover:bg-amber-700 text-xs sm:text-sm py-1.5 sm:py-2"
                  >
                    <UserGroupIcon className="w-4 h-4 mr-1" />
                    Assign Karigars
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Refund Alert */}
          {hasRefund &&
            (order.refundDetails.refundStatus === "REFUND_PENDING" ||
              order.refundDetails.refundStatus ===
                "REFUND_PENDING_APPROVAL") && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <RefreshIcon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-orange-900 text-sm sm:text-base mb-0.5 sm:mb-1">
                      Refund Required
                    </h3>
                    <p className="text-xs sm:text-sm text-orange-800 mb-2">
                      Customer is due{" "}
                      <strong>
                        {formatCurrency(order.refundDetails.refundAmount || 0)}
                      </strong>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {order.refundDetails.approvalRequired ? (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleApproveRefund}
                          className="text-xs sm:text-sm py-1.5 sm:py-2"
                        >
                          Approve Refund
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={handleProcessRefund}
                          className="text-xs sm:text-sm py-1.5 sm:py-2"
                        >
                          Process Refund
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Quick Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4 md:mb-6">
          <SummaryCard
            label="Total Amount"
            value={formatCurrency(order.orderSummary?.finalTotals?.grandTotal)}
            variant="primary"
          />
          <SummaryCard
            label="Items"
            value={order.orderSummary?.totalItems || 0}
          />
          <SummaryCard
            label="Payment"
            badge={
              <Badge
                variant={getPaymentColor(order.payment?.status)}
                size="sm"
                className="text-[9px] sm:text-[10px]"
              >
                {order.payment?.status}
              </Badge>
            }
          />
          <SummaryCard
            label="Karigar"
            badge={
              hasKarigarAssignments ? (
                <Badge
                  variant={allKarigarWorkComplete ? "green" : "blue"}
                  size="sm"
                  className="text-[9px] sm:text-[10px]"
                >
                  {karigarAssignments.length} Assigned
                </Badge>
              ) : (
                <Badge
                  variant="gray"
                  size="sm"
                  className="text-[9px] sm:text-[10px]"
                >
                  N/A
                </Badge>
              )
            }
          />
          <SummaryCard
            label={hasRefund ? "Refund Due" : "Amount Due"}
            value={formatCurrency(
              hasRefund
                ? order.refundDetails.refundAmount || 0
                : order.orderSummary?.amountDue,
            )}
            variant={hasRefund ? "warning" : "danger"}
          />
        </div>

        {/* Quick Actions Bar */}
        <div className="mb-3 sm:mb-4 md:mb-6">
          <QuickActionsBar
            order={order}
            paymentStatus={paymentStatus}
            onUpdateStatus={handleUpdateStatus}
            onUpdatePayment={() => setShowPaymentModal(true)}
            formatCurrency={formatCurrency}
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 overflow-x-auto">
            <div className="flex min-w-max">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap border-b-2 ${
                    activeTab === tab.id
                      ? "text-primary border-primary bg-primary/5"
                      : "text-gray-500 hover:text-gray-700 border-transparent"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">
                    {tab.label.length > 6
                      ? tab.label.substring(0, 6) + "..."
                      : tab.label}
                  </span>
                  {tab.count > 0 && (
                    <span className="ml-1 px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-xs bg-gray-100 rounded-full">
                      {tab.count}
                    </span>
                  )}
                  {tab.badge && (
                    <span className="ml-1 text-[10px] sm:text-xs">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-3 sm:p-4 md:p-6">
            {activeTab === "overview" && (
              <OverviewTab
                order={order}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                hasExchange={hasExchange}
                totalExchangeValue={totalExchangeValue}
                hasRefund={hasRefund}
              />
            )}
            {activeTab === "items" && (
              <ItemsTab
                order={order}
                formatCurrency={formatCurrency}
                itemsWithKarigarStatus={itemsWithKarigarStatus}
                getKarigarStatusColor={getKarigarStatusColor}
                onAssignKarigar={handleOpenKarigarAssignment}
                canAssign={canAssignKarigar}
              />
            )}
            {activeTab === "exchange" && (
              <ExchangeTab
                order={order}
                formatCurrency={formatCurrency}
                totalExchangeValue={totalExchangeValue}
              />
            )}
            {activeTab === "payment" && (
              <PaymentTab
                order={order}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                hasExchange={hasExchange}
                totalExchangeValue={totalExchangeValue}
                hasRefund={hasRefund}
                paymentStatus={paymentStatus}
                onUpdatePayment={() => setShowPaymentModal(true)}
              />
            )}
            {activeTab === "refund" && (
              <RefundTab
                order={order}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                getRefundColor={getRefundColor}
                formatRefundStatus={formatRefundStatus}
                onApprove={handleApproveRefund}
                onProcess={handleProcessRefund}
              />
            )}
            {activeTab === "karigar" && (
              <KarigarTab
                order={order}
                karigarAssignments={karigarAssignments}
                itemsWithKarigarStatus={itemsWithKarigarStatus}
                itemsWithoutKarigar={itemsWithoutKarigar}
                itemsWithKarigar={itemsWithKarigar}
                canAssignKarigar={canAssignKarigar}
                allItemsHaveKarigar={allItemsHaveKarigar}
                allKarigarWorkComplete={allKarigarWorkComplete}
                onAssignKarigar={handleOpenKarigarAssignment}
                onViewQueue={handleViewKarigarQueue}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                getKarigarStatusColor={getKarigarStatusColor}
                loading={karigarLoading}
              />
            )}
            {activeTab === "approval" && (
              <ApprovalTab order={order} formatDate={formatDate} />
            )}
            {activeTab === "timeline" && (
              <TimelineTab order={order} formatDate={formatDate} />
            )}
            {activeTab === "fulfillment" && (
              <FulfillmentManager
                orderId={order.orderId}
                order={order}
                isAdmin={true}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={showApprovalModal}
        closeModal={() => setShowApprovalModal(false)}
        title="Approve Order"
      >
        <div className="space-y-4">
          <p className="text-sm sm:text-base text-gray-600">
            Are you sure you want to approve this order? Prices will be locked
            after approval.
          </p>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Approval Notes (Optional)
            </label>
            <textarea
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Add any notes about this approval..."
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => setShowApprovalModal(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={handleApprove}
              className="w-full sm:w-auto"
            >
              Approve Order
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showRejectModal}
        closeModal={() => setShowRejectModal(false)}
        title="Reject Order"
      >
        <div className="space-y-4">
          <p className="text-sm sm:text-base text-gray-600">
            Please provide a reason for rejecting this order.
          </p>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Rejection Reason *
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Explain why this order is being rejected..."
              required
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => setShowRejectModal(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleReject}
              className="w-full sm:w-auto"
            >
              Reject Order
            </Button>
          </div>
        </div>
      </Modal>

      {/* Refund Method Selection Modal */}
      <Modal
        isOpen={showRefundMethodModal}
        closeModal={() => setShowRefundMethodModal(false)}
        title="Specify Refund Method"
        maxWidth="md"
      >
        <RefundMethodModal
          order={order}
          refundMethod={refundMethod}
          setRefundMethod={setRefundMethod}
          refundBankDetails={refundBankDetails}
          setRefundBankDetails={setRefundBankDetails}
          refundUpiDetails={refundUpiDetails}
          setRefundUpiDetails={setRefundUpiDetails}
          onConfirm={handleConfirmWithRefundMethod}
          onCancel={() => setShowRefundMethodModal(false)}
          formatCurrency={formatCurrency}
        />
      </Modal>

      {/* Payment Update Modal */}
      {showPaymentModal && (
        <PaymentUpdateModal
          order={order}
          paymentStatus={paymentStatus}
          onClose={() => setShowPaymentModal(false)}
          onUpdate={handleUpdatePayment}
          formatCurrency={formatCurrency}
          getPaymentColor={getPaymentColor}
        />
      )}

      {/* Karigar Assignment Modal */}
      {showAssignmentModal && (
        <KarigarAssignmentModal
          order={order}
          itemIndex={selectedItemIndex}
          productKarigars={bulkProductKarigars}
          otherKarigars={bulkOtherKarigars}
          recommendations={bulkRecommendations}
          summary={bulkSummary}
          loading={karigarLoading.bulkAvailability}
          onClose={closeAssignmentModal}
          onAssign={handleAssignKarigar}
          onViewQueue={handleViewKarigarQueue}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Karigar Queue Modal */}
      {showQueueModal && (
        <KarigarQueueModal
          karigarId={selectedKarigarId}
          queue={karigarQueue}
          loading={karigarLoading.karigarQueue}
          onClose={closeQueueModal}
          formatDate={formatDate}
        />
      )}

      {/* Bill Export Modal */}
      {showBill && (
        <BillExport order={order} onClose={() => setShowBill(false)} />
      )}
    </div>
  );
};

// ==========================================
// SUMMARY CARD COMPONENT
// ==========================================

const SummaryCard = ({ label, value, badge, variant = "default" }) => {
  const variantClasses = {
    default: "bg-white",
    primary: "bg-gradient-to-br from-primary/5 to-primary/10",
    warning: "bg-gradient-to-br from-orange-50 to-amber-50",
    danger: "bg-gradient-to-br from-red-50 to-rose-50",
  };

  const valueClasses = {
    default: "text-gray-900",
    primary: "text-primary",
    warning: "text-orange-600",
    danger: "text-red-600",
  };

  return (
    <div
      className={`${variantClasses[variant]} rounded-lg sm:rounded-xl p-2.5 sm:p-3 md:p-4 border border-gray-200 shadow-sm`}
    >
      <p className="text-[10px] sm:text-xs text-gray-500 mb-1 truncate">
        {label}
      </p>
      {badge ? (
        badge
      ) : (
        <p
          className={`text-base sm:text-lg md:text-xl font-bold ${valueClasses[variant]} truncate`}
        >
          {value}
        </p>
      )}
    </div>
  );
};

export default OrderDetails;
